import React, {useEffect} from "react";
import {
    useListContext,
    List,
    Datagrid,
    TextField,
    BooleanField,
    NumberField,
    EditButton,
    Create,
    Edit,
    SimpleForm,
    TextInput,
    BooleanInput,
    NumberInput,
    SelectInput,
    required,
    minLength,
    maxLength,
    number,
    regex, useRecordContext
} from 'react-admin';
import {Stack, Grid, Typography, Box, Button} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {useFormContext, useWatch} from 'react-hook-form';

// Reusable choices for SelectInputs
const buttonPositioningOptions = [
    {id: 'left', name: 'Links'},
    {id: 'center', name: 'Mitte'},
    {id: 'right', name: 'Rechts'},
    {id: 'fullwidth', name: 'Volle Breite'},
];

const buttonColorOptions = [
    {id: 'primary', name: 'Primär'},
    {id: 'secondary', name: 'Sekundär'},
    {id: 'custom', name: 'Benutzerdefiniert'},
];

const textColorOptions = [
    {id: 'dark', name: 'Dunkel'},
    {id: 'light', name: 'Hell'},
    {id: 'custom', name: 'Benutzerdefiniert'},
];

const linkTargetOptions = [
    {id: '_blank', name: 'Neuer Reiter (_blank)'},
    {id: '_self', name: 'Gleicher Reiter (_self)'},
];

const validateCssColor = [
    regex(
        /^(#([A-Fa-f0-9]{3,4}){1,2}|(rgb|hsl)a?\(\s*\d+\s*,\s*\d+%?\s*,\s*\d+%?\s*(,\s*[0-1]?\.?\d+)?\)|transparent|aliceblue|antiquewhite|aqua|aquamarine|azure|beige|bisque|black|blanchedalmond|blue|blueviolet|brown|burlywood|cadetblue|chartreuse|chocolate|coral|cornflowerblue|cornsilk|crimson|cyan|darkblue|darkcyan|darkgoldenrod|darkgray|darkgreen|darkgrey|darkkhaki|darkmagenta|darkolivegreen|darkorange|darkorchid|darkred|darksalmon|darkseagreen|darkslateblue|darkslategray|darkslategrey|darkturquoise|darkviolet|deeppink|deepskyblue|dimgray|dimgrey|dodgerblue|firebrick|floralwhite|forestgreen|fuchsia|gainsboro|ghostwhite|gold|goldenrod|gray|green|greenyellow|grey|honeydew|hotpink|indianred|indigo|ivory|khaki|lavender|lavenderblush|lawngreen|lemonchiffon|lightblue|lightcoral|lightcyan|lightgoldenrodyellow|lightgray|lightgreen|lightgrey|lightpink|lightsalmon|lightseagreen|lightskyblue|lightslategray|lightslategrey|lightsteelblue|lightyellow|lime|limegreen|linen|magenta|maroon|mediumaquamarine|mediumblue|mediumorchid|mediumpurple|mediumseagreen|mediumslateblue|mediumspringgreen|mediumturquoise|mediumvioletred|midnightblue|mintcream|mistyrose|moccasin|navajowhite|navy|oldlace|olive|olivedrab|orange|orangered|orchid|palegoldenrod|palegreen|paleturquoise|palevioletred|papayawhip|peachpuff|peru|pink|plum|powderblue|purple|rebeccapurple|red|rosybrown|royalblue|saddlebrown|salmon|sandybrown|seagreen|seashell|sienna|silver|skyblue|slateblue|slategray|slategrey|snow|springgreen|steelblue|tan|teal|thistle|tomato|turquoise|violet|wheat|white|whitesmoke|yellow|yellowgreen)$/i,
        'Muss ein gültiger CSS-Farbwert sein (z.B. #RRGGBB, rgb(0,0,0), transparent oder eine benannte Farbe)'
    )
];

const validateUrl = [
    regex(
        /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$|^(\/|\.\/|\.\.\/)[^\s]*$|^[^\s/$.?#][^\s]*$/i,
        'Muss eine gültige URL sein (beginnend mit http(s)://, ftp://, /, ./, ../ oder ein relativer Pfad)'
    )
];

const ConditionalButtonVisibilityInput = () => {
    const {setValue} = useFormContext();
    const linkUrl = useWatch({name: 'link.linkUrl'});
    const isDisabled = !linkUrl;

    useEffect(() => {
        if (isDisabled) {
            setValue('link.buttonVisibility', false, {shouldDirty: true});
        }
    }, [isDisabled, setValue]);

    return <BooleanInput source="link.buttonVisibility" label="Button sichtbar?" disabled={isDisabled}/>;
};

const CustomAssetURLField = (props) => {
    const record = useRecordContext();
    return (
        <Stack direction="row" spacing={2}>
            <Button
                href={props.assetSize === 'md' ? record.assets.md : record.assets.sm}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNewIcon/>}>
                Vorschau
            </Button>
        </Stack>
    )
};

const ECBForm = (props) => (
    <SimpleForm {...props}>
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant="h4">
                    {props.title}
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="h5">Grundeinstellungen</Typography>
            </Grid>
            <Grid item xs={12}>
                <BooleanInput source="isVisible" label="ECB ist Sichtbar?"/>
            </Grid>
            <Grid item xs={12} md={6}>
                <TextInput
                    source="assets.md"
                    label="Asset-URL (Desktop)"
                    fullWidth
                    validate={[required('Desktop Asset-URL ist erforderlich'), ...validateUrl]}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextInput
                    source="assets.sm"
                    label="Asset-URL (Mobil)"
                    fullWidth
                    validate={[required('Mobil Asset-URL ist erforderlich'), ...validateUrl]}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextInput
                    source="altText"
                    label="Alternativtext"
                    fullWidth
                    validate={[required('Alternativtext ist erforderlich'), minLength(5, 'Mindestens 5 Zeichen'), maxLength(255, 'Maximal 255 Zeichen')]}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <NumberInput
                    source="order"
                    label="Position in Projektliste (Nummerierung beginnt bei 0)"
                    fullWidth
                    validate={[required('Position ist erforderlich'), number('Muss ein numerischer Wert sein')]}
                />
            </Grid>
        </Grid>

        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant="h6" style={{marginBottom: "20px"}}>Link-Konfiguration</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
                <TextInput
                    source="link.linkUrl"
                    label="Link-URL"
                    fullWidth
                    validate={validateUrl}
                    helperText={"Wird für Link-URL ein leerer Wert übergeben, wird das ECB nicht verlinkt. Die Einstellungen für das Link-Ziel und den Button werden damit ebenfalls ignoriert."}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <SelectInput
                    style={{marginTop: 0}}
                    source="link.linkTarget"
                    label="Link-Ziel"
                    choices={linkTargetOptions}
                    fullWidth
                    validate={[required('Link-Ziel ist erforderlich')]}
                />
            </Grid>
        </Grid>

        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant="h6" style={{marginBottom: "20px", marginTop: "20px"}}>Button-Konfiguration</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
                <ConditionalButtonVisibilityInput/>
            </Grid>
            <Grid item xs={12} md={3}>
                <BooleanInput source="link.outline" label="Kontur-Button?"/>
            </Grid>
            <Grid item xs={12}>
                <TextInput
                    source="link.buttonText"
                    label="Button-Text"
                    fullWidth
                    validate={[required('Button-Text ist erforderlich'), minLength(2, 'Mindestens 2 Zeichen'), maxLength(50, 'Maximal 50 Zeichen')]}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <SelectInput
                    source="link.buttonColor"
                    label="Hintergrundfarbe"
                    choices={buttonColorOptions}
                    fullWidth
                    validate={[required('Farbe ist erforderlich')]}
                    style={{marginTop: 0}}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextInput
                    source="link.customButtonColor"
                    label="Benutzerdefinierte Hintergrundfarbe (Hex-Wert)"
                    fullWidth
                    validate={validateCssColor}
                    helperText="Nur aktiv, wenn Hintergrundfarbe 'Benutzerdefiniert' gewählt ist."
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <SelectInput
                    source="link.buttonTextColor.mode"
                    label="Textfarbe"
                    choices={textColorOptions}
                    fullWidth
                    validate={[required('Textfarbe ist erforderlich')]}
                    style={{marginTop: 0}}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextInput
                    source="link.buttonTextColor.custom"
                    label="Benutzerdefinierte Textfarbe (Hex-Wert)"
                    fullWidth
                    validate={validateCssColor}
                    helperText="Nur aktiv, wenn Textfarbe 'Benutzerdefiniert' ist."
                />
            </Grid>
        </Grid>

        <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
                <SelectInput
                    source="link.buttonPositioning.md"
                    label="Button-Position (Desktop)"
                    choices={buttonPositioningOptions}
                    fullWidth
                    validate={[required('Desktop Button-Position ist erforderlich')]}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <SelectInput
                    source="link.buttonPositioning.sm"
                    label="Button-Position (Mobile)"
                    choices={buttonPositioningOptions}
                    fullWidth
                    validate={[required('Mobil Button-Position ist erforderlich')]}
                />
            </Grid>
        </Grid>
    </SimpleForm>
);

// List Component
export const ECBList = (props) => (
    <List {...props}>
        <Datagrid>
            <BooleanField source="isVisible" label="ECB sichtbar?"/>
            <NumberField source="order" label="Position"/>
            <TextField source="link.linkUrl" label="Link-URL"/>
            <TextField source="link.buttonText" label="Button-Text"/>
            <BooleanField source="link.buttonVisibility" label="Button sichtbar?"/>
            <CustomAssetURLField label="Asset (Mobile)" assetSize={'sm'}/>
            <CustomAssetURLField label="Asset (Desktop)" assetSize={'md'}/>
            <TextField source="altText" label="Alternativtext"/>
            <EditButton/>
        </Datagrid>
    </List>
);

export const ECBCreate = (props) => (
    <Create {...props}>
        <ECBForm {...props} title="Neues Embedded Customer Branding anlegen"/>
    </Create>
);

export const ECBEdit = (props) => (
    <Edit {...props} redirect="list">
        <ECBForm {...props} title="Embedded Customer Branding bearbeiten"/>
    </Edit>
);